import React, { useState, useEffect } from "react";
import styles from "./BackgroundApprovalViewer.module.css";
import { fetchDocumentForClient } from "./BackgroundApprovalViewer.service";
import * as Icon from "react-bootstrap-icons";
import {
  BackgroundApprovalViewerProps,
  ClientDocument,
} from "./BackgroundApprovalViewer.utils";
import { FileInputTypes } from "../../ClientApprovalWizard.utils";
import { Col, Row } from "react-bootstrap";
import PdfViewer from "../../../../reusable_components/PdfViewer/PdfViewer";
import SpreadsheetViewer from "../../../../../Reusables/components/SpreadsheetViewer/SpreadsheetViewer";
import WordDocumentViewer from "../../../../../Reusables/components/WordDocumentViewer/WordDocumentViewer";
import ImageViewer from "../../../../../Reusables/components/ImageViewer/ImageViewer";
import TextViewer from "../../../../../Reusables/components/TextViewer/TextViewer";
import { getFileIcon } from "../../../../client_onboarding/ClientCreator/components/DocumentUploader/DocumentUploader";
import { downloadFile } from "../../../../client_onboarding/ClientCreator/components/DocumentUploader/DocumentUploader.utils";

/**
 * BackgroundApprovalViewer Parent Component
 * This component serves as the main container and can have child components inside "components/" folder.
 */
const BackgroundApprovalViewer: React.FC<BackgroundApprovalViewerProps> = ({
  sharedData,
  setSharedData,
  onValidationChange,
}) => {
  const [existingDocuments, setExistingDocuments] = useState<{
    [firmReferenceCode: string]: ClientDocument[]; // or more specific type if available
  }>({});
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    console.log("sharedData: ", sharedData);
  }, [sharedData]);

  const fetchExistingDocument = async (
    clientDocumentId: number,
    documentTypeCode: string
  ) => {
    const data = await fetchDocumentForClient(
      sharedData.clientMasterId,
      (progress) => {},
      documentTypeCode,
      clientDocumentId
    );
    if (data) {
      setExistingDocuments((prev) => {
        const updated = { ...prev, [documentTypeCode]: data };

        // Show first document automatically if none currently previewed
        if (!previewFile && data.length > 0) {
          const firstFile = data[0];
          setPreviewFile({
            url: firstFile.BlobUrl,
            name: firstFile.FileName,
          });
        }

        return updated;
      });
    }
  };

  useEffect(() => {
    if (sharedData.fileInputs?.length > 0) {
      sharedData.fileInputs.forEach((input: FileInputTypes) => {
        fetchExistingDocument(
          input.FirmReferenceDataId,
          input.ReferenceDataCode
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedData.fileInputs]);

  return (
    sharedData.fileInputs && (
      <>
        <Row>
          <Col md={4}>
            <div className={styles.filesViewerWrapper}>
              {sharedData.fileInputs.map(
                (input: FileInputTypes, index: number) =>
                  existingDocuments[input.ReferenceDataCode]?.length > 0 && (
                    <div>
                      <div className={styles.uploadFilesContent}>
                        <div>
                          <strong>{input.ReferenceDataValue}</strong>
                        </div>
                        <div
                          className={
                            styles.existingFileInfo +
                            " " +
                            (existingDocuments[input.ReferenceDataCode]
                              ?.length > 0
                              ? styles.availableFiles
                              : "")
                          }
                        >
                          {existingDocuments[input.ReferenceDataCode]?.length ||
                            0}
                        </div>
                      </div>
                      <div className={styles.filesListWrapper}>
                        {existingDocuments[input.ReferenceDataCode]?.length >
                          0 && (
                          <div className={styles.uploadedFileList}>
                            {existingDocuments[input.ReferenceDataCode].map(
                              (file, idx) => (
                                <div
                                  key={idx}
                                  className={styles.uploadedFileRow}
                                >
                                  <div className={styles.uploadedFileName}>
                                    {getFileIcon(file.FileName)}
                                    <span>{file.FileName}</span>
                                  </div>

                                  <div className={styles.uploadedFileActions}>
                                    <Icon.Eye
                                      className={styles.viewIcon}
                                      title="View Document"
                                      onClick={() =>
                                        setPreviewFile({
                                          url: file.BlobUrl,
                                          name: file.FileName,
                                        })
                                      }
                                    />
                                    <Icon.Download
                                      className={styles.downloadIcon}
                                      title="Download Document"
                                      onClick={() => downloadFile(file.BlobUrl, file.FileName)}
                                    />
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
              )}
            </div>
          </Col>
          <Col md={8}>
            <div className={styles.viewerWrapper}>
              {/\.xlsx$|\.xls$|\.csv$/i.test(previewFile?.url || "") ? (
                <SpreadsheetViewer preview={previewFile} />
              ) : /\.docx$|\.doc$/i.test(previewFile?.url || "") ? (
                <WordDocumentViewer preview={previewFile!!} />
              ) : /\.(jpg|jpeg|png|gif|bmp|tiff|webp|svg)$/i.test(previewFile?.url || "") ? (
                <ImageViewer preview={previewFile} />
              ) : /\.(txt|log|json|xml|yaml|yml|md|rtf)$/i.test(previewFile?.url || "") ? (
                <TextViewer preview={previewFile} />
              ) : (
                <PdfViewer preview={previewFile} />
              )}
            </div>
          </Col>
        </Row>
      </>
    )
  );
};

export default BackgroundApprovalViewer;
